/**
 * Officer pivot (PIVOT01). An officer asked one bounded question of this finished record; the model chose which pages to
 * read, the engine read them and recorded each read, and every sentence below cites a record fact, a pivot row or a
 * transaction hash. The heading says what the grade is: a pivot is a LEAD. It is not a trace, a label, a notice row or a
 * matrix row, and it changes no figure of the report. The ledger lines name every page read with its provider and time.
 */
import { esc } from '../../../utils/reportCommon';
import type { PivotBlock, PivotCited, PivotLedgerEntry, PivotRead } from '../../../types/pivot';

export const PIVOT_TITLE = 'Officer pivot (lead)';

export const STOP_WORDS: Record<string, string> = {
  deadline: 'the time budget ran out',
  cost_cap: 'the cost cap of the pivot was reached',
  budget: 'a model cost cap refused the call',
  policy: 'the data-residency policy refused the call',
  no_answer: 'the model did not reach an answer within its rounds',
  no_key: 'no model key is configured',
  llm_disabled: 'the model switch is off',
  no_facts: 'the record held no facts',
  invalid_json: 'the model answered with text that is not JSON',
  invalid_shape: 'the model answered in a shape the pass does not accept',
  call_failed: 'the model call failed',
  record_unreadable: 'the record could not be read',
  missing_coverage_note: 'the model answered without saying what its answer covers and what it does not',
};

const n = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const short = (s: string) => (s.length > 14 ? `${s.slice(0, 8)}…` : s);

function citeLink(blockId: string, c: string, cited: Record<string, PivotCited>): string {
  const kind = cited[c]?.kind;
  if (kind === 'hash') return `<a href="#appendix-a1" data-pivot-cite="hash" title="${esc(c)}">${esc(short(c))}</a>`;
  if (kind === 'pivot') return `<a href="#pivot-cite-${esc(blockId)}-${esc(c)}" data-pivot-cite="pivot">${esc(c)}</a>`;
  return `<a href="#pivot-cite-${esc(blockId)}-${esc(c)}" data-pivot-cite="fact">${esc(c)}</a>`;
}

function readLine(r: PivotRead): string {
  if (r.kind === 'transaction') {
    return `read ${r.seq}: transfer ${esc(short(r.address))} on ${esc(r.chain)} · ${esc(r.provider || 'provider not recorded')} · ${esc(r.at || 'time not recorded')}`
      + `${r.error ? ` · failed (${esc(r.error)})` : ''}`;
  }
  const extent = r.error && !r.rows ? `failed (${esc(r.error)})` : `${n(r.rows)} transfer(s), ${r.cut ? 'page full, older history not read' : 'whole history returned'}`;
  return `read ${r.seq}: page of ${esc(short(r.address))} on ${esc(r.chain)} · ${esc(r.provider || 'provider not recorded')} (${esc(r.source || 'source not recorded')}) · `
    + `${esc(r.at || 'time not recorded')} · ${extent}`;
}

function ledgerEntry(e: PivotLedgerEntry): string {
  const args = Object.entries(e.arguments || {}).filter(([k]) => k !== 'chain')
    .map(([k, v]) => `${esc(k)} ${esc(short(String(v)))}`).join(', ');
  const stop = e.stopped ? ` · stopped by the ${esc(e.stopped)} budget` : '';
  const why = e.status === 'refused' ? ` · refused (${esc(e.reason || 'no reason recorded')})` : '';
  const notes = (e.notes || []).map((x) => `<div class="muted" data-testid="pivot-note">${esc(x)}</div>`).join('');
  const reads = (e.reads || []).map((r) => `<div class="muted" data-testid="pivot-read">${readLine(r)}</div>`).join('');
  return `<li data-testid="pivot-ledger-entry" data-pivot-tool="${esc(e.tool)}" data-pivot-status="${esc(e.status)}">`
    + `Round ${n(e.round)}: <strong>${esc(e.tool)}</strong> (${args || 'no arguments'}) · ${esc(e.status)}${stop}${why} · ${(e.rows || []).length} row(s) recorded${notes}${reads}</li>`;
}

/** The tool-call list of a block (reads with provider and time) as HTML; the panel shows it under "Tool calls". */
export function pivotLedgerHtml(b: PivotBlock): string {
  return (b.ledger || []).length
    ? `<ul data-testid="pivot-ledger">${(b.ledger || []).map(ledgerEntry).join('')}</ul>` : '';
}

/** One pivot block as HTML (every model-written or model-chosen string is escaped). */
export function pivotBlockHtml(b: PivotBlock): string {
  const cited = b.cited || {};
  const c = b.counts || {};
  const statements = (b.statements || []).filter((s) => s && Array.isArray(s.cites) && s.cites.length > 0);
  const head = `<p><strong>Question:</strong> <span data-testid="pivot-question">${esc(b.question)}</span></p>`;
  const used = b.used || {};
  const bud = b.budget || {};
  const budgetLine = `<p class="muted" data-testid="pivot-budget">Used ${n(used.pages)} of ${n(bud.pages)} page reads, ${n(used.seconds).toFixed(0)} of ${n(bud.seconds)} s, `
    + `${n(used.rounds)} of ${n(bud.rounds)} model rounds.</p>`;
  const state = b.status === 'answered' ? ''
    : `<p data-testid="pivot-status" data-pivot-status="${esc(b.status)}">The pivot ${b.status === 'stopped' ? 'stopped before it could answer' : b.status === 'off' ? 'did not run' : 'failed'}: `
      + `${esc(STOP_WORDS[String(b.reason || '')] || b.reason || 'no reason recorded')}. The rows it found are listed below.</p>`;
  let answer = '';
  if (b.status === 'answered') {
    answer = statements.length
      ? `<ol data-testid="pivot-statements">${statements.map((s) => `<li data-testid="pivot-statement">${esc(s.text)} `
        + `<span class="muted">[${s.cites.map((x) => citeLink(b.pivot_id, x, cited)).join(', ')}]</span> `
        + '<span class="chip" data-pivot-grade="lead">lead</span></li>').join('')}</ol>`
      : `<p data-testid="pivot-empty">The pivot produced no statement that cites the record or its page reads `
        + `(${n(c.dropped_uncited)} uncited and ${n(c.dropped_unknown_cite)} with a citation outside the record were dropped).</p>`;
  }
  const coverage = b.coverage_note
    ? `<p data-testid="pivot-coverage"><strong>What this covers:</strong> ${esc(b.coverage_note)}</p>` : '';
  const dropped = statements.length && (n(c.dropped_uncited) || n(c.dropped_unknown_cite) || n(c.dropped_malformed))
    ? `<p class="muted" data-testid="pivot-dropped">${n(c.dropped_uncited)} statement(s) dropped for citing nothing, ${n(c.dropped_unknown_cite)} for a citation outside the record`
      + `${n(c.dropped_malformed) ? `, ${n(c.dropped_malformed)} malformed` : ''}.</p>` : '';
  const citedIds = [...new Set(statements.flatMap((s) => s.cites))].filter((id) => cited[id] && cited[id].kind !== 'hash');
  const citedHtml = citedIds.length
    ? `<h4>Cited rows</h4><ol data-testid="pivot-cited">${citedIds.map((id) => `<li id="pivot-cite-${esc(b.pivot_id)}-${esc(id)}" data-testid="pivot-cited-row">`
      + `<strong>${esc(id)}</strong> <span class="muted">[${esc(cited[id].kind === 'pivot' ? `pivot ${cited[id].tool || ''}, round ${n(cited[id].round)}` : cited[id].block || 'record')}]</span> ${esc(cited[id].text || '')}</li>`).join('')}</ol>`
    : '';
  const ledger = (b.ledger || []).length
    ? `<h4>Ledger</h4><ul data-testid="pivot-ledger">${(b.ledger || []).map(ledgerEntry).join('')}</ul>` : '';
  return `<div data-testid="pivot-block" data-pivot-id="${esc(b.pivot_id)}" data-pivot-grade="lead">${head}${state}${answer}${coverage}${dropped}${citedHtml}${ledger}${budgetLine}</div>`;
}

/** The report section body for the pivots of the shown run, or '' when there are none. */
export function pivotSectionHtml(blocks: readonly PivotBlock[] | null | undefined): string {
  const list = (blocks || []).filter((b) => b && typeof b === 'object' && b.pivot_id);
  if (!list.length) return '';
  const intro = '<p class="muted">An officer asked the questions below of this finished record. The model chose which pages to read; the engine read them and recorded each read. '
    + 'Every statement cites the record or a recorded page read, and statements that cite nothing were dropped. '
    + 'A pivot is a lead for the officer to check: it is not a trace, a label, a notice row or a seizure-matrix row, and it changes no figure of this report.</p>';
  return `<div data-testid="pivot-section">${intro}${list.map(pivotBlockHtml).join('<hr/>')}</div>`;
}
